import { Component, Input, OnInit, OnChanges, SimpleChanges, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { firstValueFrom, Observable } from 'rxjs';

import { DefinitionControllerService, AiFunctionCallControllerService, ProjectModelFieldControllerService, Definition } from '@rapider/sdk';
import { TemplateRendererComponent } from '@rapider/template-renderer';
import { RapiderJsonViewerComponent } from '@rapider/components/json-viewer';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { selectProjectModelsList } from '@rapider/project-data';
import { NotificationService } from '@rapider/services';

import { DatabaseActions } from '../state/database.actions';
import { selectDataCardTemplates } from '../state/data-history.reducer';
import { DATABASE_CONFIG, buildDataCardPrompt, buildImproveDataCardPrompt, buildFieldsFilter, extractTemplateFromAiResponse, extractTemplateFromDefinitionValue, DefinitionFileNames } from '../database.config';
import { AiPromptComponent, AiPromptSuggestion } from '@rapider/components/ai-prompt';

/**
 * @class DataCardComponent
 * @description A smart UI component that displays a single record using an AI-generated Handlebars template.
 * It intelligently caches templates in NgRx, queries the DB for existing templates, and orchestrates
 * real-time AI generation if a template doesn't yet exist for the specified Project Model.
 * It features a 3D flip interaction to toggle seamlessly between the front user-friendly template view
 * and the backface raw JSON data view.
 */
@Component({
  selector: 'rapider-data-card',
  standalone: true,
  imports: [CommonModule, TemplateRendererComponent, RapiderJsonViewerComponent, NzTagModule, NzModalModule, AiPromptComponent],
  templateUrl: './data-card.component.html'
})
export class DataCardComponent implements OnInit, OnChanges {
  private readonly store = inject(Store);
  private readonly definitionController = inject(DefinitionControllerService);
  private readonly aiFunctionController = inject(AiFunctionCallControllerService);
  private readonly projectModelFieldController = inject(ProjectModelFieldControllerService);
  private readonly notificationService = inject(NotificationService);

  @Input({ required: true }) projectModelName!: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input({ required: true }) data!: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() diffClassMap: any = {};

  public readonly config = DATABASE_CONFIG;

  public isLoading = signal<boolean>(false);
  public isGenerating = signal<boolean>(false);
  public isFlipped = signal<boolean>(false);

  // Modal State Signals
  public isImproveModalVisible = signal<boolean>(false);
  public isImproving = signal<boolean>(false);
  public starterPrompts = signal<AiPromptSuggestion[]>([]); // Added for future readiness

  public cachedTemplates = this.store.selectSignal(selectDataCardTemplates);
  public projectModels = this.store.selectSignal(selectProjectModelsList);

  public activeTemplate = computed(() => {
    const templates = this.cachedTemplates();
    return templates[this.projectModelName] || null;
  });

  public modelTitle = computed(() => {
    const models = this.projectModels() || [];
    const found = models.find((m: any) =>
      (m.name || '').toLowerCase() === this.projectModelName.toLowerCase() ||
      (m.dataTableName || '').toLowerCase() === this.projectModelName.toLowerCase()
    );
    return found?.title || found?.dataTableName || found?.name || this.projectModelName;
  });

  ngOnInit() {
    this.resolveTemplate();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectModelName'] && !changes['projectModelName'].isFirstChange()) {
      this.resolveTemplate();
    }
  }

  /**
   * Toggles the 3D flip state between the Template View and the Raw JSON View.
   */
  public toggleFlip(): void {
    this.isFlipped.update(v => !v);
  }

  public openImproveModal(): void {
    this.isImproveModalVisible.set(true);
  }

  public closeImproveModal(): void {
    this.isImproveModalVisible.set(false);
  }

  /**
     * Orchestrates the 3-step resolution process for the data card template.
      * 1. Check Store (Computed automatically via activeTemplate)
      * 2. Check Database API and File Versions
      * 3. Call AI Tools to generate
      */
  private async resolveTemplate(): Promise<void> {
    if (!this.projectModelName) return;

    // STEP 1: If it's already in the NgRx store, the computed signal handles rendering instantly.
    if (this.activeTemplate()) {
      return;
    }

    this.isLoading.set(true);

    try {
      // STEP 2: Fetch from Database API (Using 'key' to match the create payload)
      const filter = JSON.stringify({
        where: { key: this.projectModelName, type: 'dataCardHtmlTemplate' }
      });

      const existingDefinitions = await firstValueFrom(this.definitionController.find({ filter }) as any);

      if (Array.isArray(existingDefinitions) && existingDefinitions.length > 0) {
        const defId = existingDefinitions[0].id;

        try {
          // Retrieve the raw HTML template through the Git-backed endpoint
          const extractedTemplate = await firstValueFrom(this.definitionController.readFileVersion({
            id: defId,
            extension: 'html',
            name: DefinitionFileNames.DataCardTemplate
          }));

          if (extractedTemplate) {
            // Found in DB -> Save to Store
            this.store.dispatch(DatabaseActions.setDataCardTemplate({
              projectModelName: this.projectModelName,
              template: extractedTemplate
            }));
            this.isLoading.set(false);
            return;
          }
        } catch (fileError) {
          console.warn('[DataCard] Template file might not exist or failed to load via version API', fileError);
        }
      }

      // STEP 3: Not found in DB -> Generate via AI
      await this.generateTemplateWithAi();

    } catch (error) {
      console.error('[DataCardComponent] Failed to resolve template:', error);
      this.isLoading.set(false);
    }
  }

  /**
   * Helper method to securely fetch and assemble the full project model context, including missing fields for relationships.
   * Isolates the logic from state mutation to keep side effects pure.
   * @returns {Promise<{ modelDetails: any, localModels: any[], includeAuditFieldsForMainModel: boolean }>} The assembled context block.
   */
  private async getEnrichedModelContext(includeAuditFieldsForMainModel = false): Promise<{ modelDetails: any, localModels: any[] }> {
    const localModels = [...(this.projectModels() || [])];

    const modelDetailsIndex = localModels.findIndex((m: any) =>
      (m.name || '').toLowerCase() === this.projectModelName.toLowerCase() ||
      (m.dataTableName || '').toLowerCase() === this.projectModelName.toLowerCase()
    );

    if (modelDetailsIndex === -1) {
      throw new Error(`Model details not found for ${this.projectModelName}`);
    }

    let modelDetails = localModels[modelDetailsIndex];

    // Fetch fields dynamically for the main model if they were not eagerly loaded in the cache
    if (!modelDetails.fields || modelDetails.fields.length === 0) {
      const fieldsFilter = buildFieldsFilter(modelDetails.id);
      const fields = await firstValueFrom(this.projectModelFieldController.find({ filter: fieldsFilter as any }));
      modelDetails = { ...modelDetails, fields };
      localModels[modelDetailsIndex] = modelDetails;
    }

    if (includeAuditFieldsForMainModel) {
      const fieldsWithoutAudit = modelDetails.fields.filter((f: any) => !f.isMixinProperty);
      modelDetails = { ...modelDetails, fields: fieldsWithoutAudit };
      localModels[modelDetailsIndex] = modelDetails;
    }

    // Iteratively check related models and fetch their fields if missing
    if (modelDetails.relations && Array.isArray(modelDetails.relations)) {
      for (const rel of modelDetails.relations) {
        const targetId = typeof rel.targetModel === 'string' ? rel.targetModel : rel.targetModel?.id;

        if (targetId) {
          const targetIndex = localModels.findIndex((m: any) => m.id === targetId);

          if (targetIndex > -1) {
            let targetModel = localModels[targetIndex];
            if (!targetModel.fields || targetModel.fields.length === 0) {
              try {
                const fieldsFilter = buildFieldsFilter(targetModel.id);
                const fields = await firstValueFrom(
                  this.projectModelFieldController.find({ filter: fieldsFilter as any })
                );
                localModels[targetIndex] = { ...targetModel, fields };
              } catch (e) {
                console.error(`[DataCardComponent] Failed to fetch fields for related model ID: ${targetId}`, e);
              }
            }
          }
        }
      }
    }

    return { modelDetails, localModels };
  }

  /**
     * Queries the cached NgRx project models for contextual details, invokes the AI generation tool, 
     * and saves the resulting artifact both to the DB and NgRx Store using Version APIs.
     */
  private async generateTemplateWithAi(): Promise<void> {
    this.isGenerating.set(true);
    this.isLoading.set(false);

    if (!this.data || Object.keys(this.data).length === 0) {
      this.isGenerating.set(false);
      return;
    }

    try {
      const { modelDetails, localModels } = await this.getEnrichedModelContext();

      // Pass this.data into the prompt builder as the sampleData payload
      const promptText = buildDataCardPrompt(this.projectModelName, modelDetails, localModels, this.data);

      // Invoke the backend AI execution
      const aiResponse = await firstValueFrom(
        this.aiFunctionController.executeFunctionToolsCall$FormData$Response({
          id: 'findByName',
          body: {
            name: 'GenerateDataCardTemplate',
            functionPrompt: promptText
          }
        })
      );

      const generatedTemplate = extractTemplateFromAiResponse(aiResponse);

      if (generatedTemplate) {
        let defId;
        const filter = JSON.stringify({ where: { key: this.projectModelName, type: 'dataCardHtmlTemplate' } });
        const existingDefinitions = await firstValueFrom(this.definitionController.find({ filter }) as any);

        if (Array.isArray(existingDefinitions) && existingDefinitions.length > 0) {
          defId = existingDefinitions[0].id;
        } else {
          // Initialize empty definition
          const newDef: Definition = await firstValueFrom(this.definitionController.create({
            body: {
              key: this.projectModelName,
              name: `${this.projectModelName} Data Card Template`,
              type: 'dataCardHtmlTemplate',
              value: {}
            }
          }) as any);
          defId = newDef.id;
        }

        // Save the HTML artifact separately via the versions file endpoint
        await firstValueFrom(this.definitionController.upsertFileVersion({
          id: defId,
          body: {
            content: generatedTemplate,
            name: DefinitionFileNames.DataCardTemplate,
            extension: 'html',
            commitMessage: `Generate Data Card for ${this.projectModelName}`
          }
        }) as any).catch(e => console.error('[DataCard] DB Save failed', e));

        // Save to NgRx Store for immediate rendering
        this.store.dispatch(DatabaseActions.setDataCardTemplate({
          projectModelName: this.projectModelName,
          template: generatedTemplate
        }));
      } else {
        throw new Error('AI returned an empty or unparsable template.');
      }

    } catch (error) {
      console.error('[DataCardComponent] AI Generation Failed:', error);
      this.notificationService.createNotification('error', 'Generation Error', 'Failed to generate layout template via AI.');
    } finally {
      this.isGenerating.set(false);
    }
  }

  /**
   * Improves the existing template by sending the current context and the user's specific feedback to the AI.
   * Saves the newly updated template upon resolution natively into the Git-backed store.
   * @param {object} event - Output from the AiPromptComponent containing the prompt string and attachments.
   */
  public async improveTemplateWithAi(event: { promptText: string, files: File[] }): Promise<void> {
    const currentTemplate = this.activeTemplate();
    if (!currentTemplate) return;

    this.isImproving.set(true);

    try {
      const { modelDetails, localModels } = await this.getEnrichedModelContext();

      const promptText = buildImproveDataCardPrompt(this.projectModelName, modelDetails, localModels, currentTemplate, event.promptText);

      const aiResponse = await firstValueFrom(
        this.aiFunctionController.executeFunctionToolsCall$FormData$Response({
          id: 'findByName',
          body: {
            name: 'GenerateDataCardTemplate',
            functionPrompt: promptText
            // Note: If files need to be processed natively, the SDK signature must be expanded to multipart FormData
          }
        })
      );

      const improvedTemplate = extractTemplateFromAiResponse(aiResponse);

      if (improvedTemplate) {
        let defId;
        const filter = JSON.stringify({ where: { key: this.projectModelName, type: 'dataCardHtmlTemplate' } });
        const existingDefinitions = await firstValueFrom(this.definitionController.find({ filter }) as any);

        if (Array.isArray(existingDefinitions) && existingDefinitions.length > 0) {
          defId = existingDefinitions[0].id;
        } else {
          // Fallback create if it mysteriously vanished
          const newDef: Definition = await firstValueFrom(this.definitionController.create({
            body: {
              key: this.projectModelName,
              name: `${this.projectModelName} Data Card Template`,
              type: 'dataCardHtmlTemplate',
              value: {}
            }
          }) as Observable<Definition>);
          defId = newDef.id;
        }

        // Commit the improved string to the repository endpoint directly
        await firstValueFrom(this.definitionController.upsertFileVersion({
          id: defId,
          body: {
            content: improvedTemplate,
            name: DefinitionFileNames.DataCardTemplate,
            extension: 'html',
            commitMessage: `Improve Data Card for ${this.projectModelName}`
          }
        }) as any);

        // 2. Dispatch to NgRx Store
        this.store.dispatch(DatabaseActions.setDataCardTemplate({
          projectModelName: this.projectModelName,
          template: improvedTemplate
        }));

        this.notificationService.createNotification('success', 'Success', 'Template improved successfully.');
        this.closeImproveModal();
      } else {
        throw new Error('AI returned an empty or unparsable template.');
      }

    } catch (error) {
      console.error('[DataCardComponent] AI Improvement Failed:', error);
      this.notificationService.createNotification('error', 'Improvement Error', 'Failed to improve template via AI.');
    } finally {
      this.isImproving.set(false);
    }
  }
}